import React, { useEffect, useState } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  Linking, 
  ScrollView, 
  useWindowDimensions,
  StyleSheet 
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

const DepositSchemeSelector = ({ data = [], initialSelectedId, onSelect }) => {
  const [selectedId, setSelectedId] = useState(initialSelectedId ?? null);
  const [showOthers, setShowOthers] = useState(false);
  const { width } = useWindowDimensions();
  
  useEffect(() => {
    setSelectedId(initialSelectedId);
  }, [initialSelectedId]);

  const handleSelect = (id) => {
    setSelectedId(id);
    onSelect?.(id);
  };

  // Filter TDS + SDS items
  const tdsSchemes = data.filter((i) => 
    i.name?.toLowerCase().includes("tds") ||
    i.name?.toLowerCase().includes("sds")
  );

  // Everything else
  const otherSchemes = data.filter(
    (i) => !tdsSchemes.includes(i)
  );
  useEffect(() => {
    const isSelectedInOthers = otherSchemes.some(
      (i) => i.open_flow_tenancy_document_scheme_id === selectedId
    );
  
    if (isSelectedInOthers) {
      setShowOthers(true);
    }
  }, [selectedId, data]);
  // Dynamic responsive width
  const itemWidth = width < 400 ? '100%' : width < 600 ? '48%' : '31%';

  const renderSchemeCard = (scheme) => {
    const isSelected = selectedId === scheme.open_flow_tenancy_document_scheme_id;

    return (
      <TouchableOpacity
        key={scheme.open_flow_tenancy_document_scheme_id}
        onPress={() => handleSelect(scheme.open_flow_tenancy_document_scheme_id)}
        activeOpacity={0.8}
        style={[
          styles.schemeItem,
          {
            width: itemWidth,
            borderColor: isSelected ? '#6200EE' : '#e0e0e0',
            borderWidth: isSelected ? 2 : 1,
            backgroundColor: isSelected ? '#f5f0ff' : '#fff'
          }
        ]}
      >
        <View style={styles.radioContainer}>
          <View style={[
            styles.radioOuter,
            { borderColor: isSelected ? '#6200EE' : '#999' }
          ]}>
            {isSelected && <View style={styles.radioInner} />}
          </View>

          <Text 
            style={styles.schemeName}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {scheme.name.trim()}
          </Text>
        </View>

        <Text style={styles.schemeAddress} numberOfLines={2}>
          {scheme.address}
        </Text>

        {scheme.link && (
          <TouchableOpacity
            onPress={() =>
              window.open(
                scheme.link.startsWith('http')
                  ? scheme.link
                  : `https://${scheme.link}`
              )
            }
            style={styles.linkContainer}
          >
            <Text style={styles.linkText} numberOfLines={1}>
              {scheme.link.replace(/^https?:\/\//, '')}
            </Text>
            <MaterialIcons name="open-in-new" size={16} color="#6200EE" />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Tenancy Deposit Scheme</Text>
      <Text style={styles.description}>
        Please select the tenancy deposit scheme (TDS) you'll use to protect this deposit. 
        Upload any additional documents to share with your tenant(s).
      </Text>

      {/* TDS SECTION */}
      <Text style={styles.sectionHeader}>TDS Schemes</Text>
      <View style={styles.gridContainer}>
        {tdsSchemes.map(renderSchemeCard)}
      </View>

      {/* COLLAPSABLE OTHER SCHEMES */}
      {!!otherSchemes.length && (
        <View style={{ marginTop: 16 }}>
          <TouchableOpacity 
            style={styles.otherHeader}
            onPress={() => setShowOthers((prev) => !prev)}
            activeOpacity={0.7}
          >
            <Text style={styles.sectionHeader}>Other Schemes</Text>
            <MaterialIcons 
              name={showOthers ? "expand-less" : "expand-more"}
              size={24}
              color="#555"
            />
          </TouchableOpacity>

          {showOthers && (
            <View style={{ marginTop: 10 }}>
              <View style={styles.gridContainer}>
                {otherSchemes.map(renderSchemeCard)}
              </View>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#eee',
    padding: 16,
    marginTop: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#9b59b6',
    marginBottom: 4,
  },
  description: {
    fontSize: 13,
    color: '#555',
    marginBottom: 16,
  },
  sectionHeader: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 10,
    color: '#444',
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  schemeItem: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    minHeight: 110,
  },
  radioContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#6200EE',
  },
  schemeName: {
    fontWeight: '600',
    fontSize: 14,
    flex: 1,
  },
  schemeAddress: {
    fontSize: 12,
    color: '#555',
    lineHeight: 16,
  },
  linkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  linkText: {
    fontSize: 12,
    color: '#6200EE',
    textDecorationLine: 'underline'
  },
  otherHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  }
});

export default DepositSchemeSelector;
